import React, { useRef, useState } from 'react';
import {
  Typography,
  Modal,
  Layout,
  MenuProps,
  Menu,
  ConfigProvider,
  Space,
  Pagination,
  Row,
  Col,
} from 'antd';

import Loader from './Loader';
import RecordsList from '../Content/RecordList';
import { FilterBar } from '../Header/FilterBar';
import RecordItem from '../Content/RecordItem';
import ListItem from '../Content/ListItem';
import EmptyRecord from '../Content/EmptyRecord';
import DateTitle from '../Content/DateTitle';
import PageHeading from './PageHeading';
import PdfViewer from '../PDF/PDFViewer';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import {
  logWhatsTrendingEvent,
  logInformedInvestorEvent,
  logResearchReportEvent,
} from '../../services/ApiActions/whatsTrendingAct';
import { logMarketingEvent } from '../../services/ApiActions/marketingAct';
import ReactMarkdown from 'react-markdown';
import { title } from 'process';
import rehypeRaw from 'rehype-raw';
import DOMpurify from 'dompurify';

const { Sider, Content } = Layout;
const { Title } = Typography;

type MenuItem = Required<MenuProps>['items'][number];

interface ResearchLayoutProps {
  showMarkDown?: boolean;
  pagination?: React.ReactNode;
  tabs: any;
  title: string;
  object?: string;
  records: any[];
  selectedMenu?: any;
  menuItems: any;
  isLoading?: boolean;
  defaultDateRange?: any;
  selectedTab: any;
  onRangeChange?: any;
  onPriorityChange?: any;
  priority?: any;
  onAllClick: any;
  onTabClick: any;
  isNewsletter?: boolean;
  showPriorityFilter?: boolean;
  tabItemsCount?: any;
  showDateRange?: boolean;
  dateFilterComponent?: React.ReactNode;
  selectedDateOption?: string;
  headerPixels?: string;
  brandsData?: any;
}

const ResearchLayout: React.FC<ResearchLayoutProps> = ({
  brandsData,
  showMarkDown,
  pagination,
  tabs,
  title,
  object,
  records,
  selectedMenu,
  menuItems,
  isLoading,
  defaultDateRange,
  onRangeChange,
  onAllClick,
  selectedTab,
  onTabClick,
  isNewsletter,
  onPriorityChange,
  priority,
  showPriorityFilter,
  tabItemsCount,
  showDateRange,
  dateFilterComponent,
  selectedDateOption,
  headerPixels,
}) => {
  const [open, setOpen] = useState(false);
  const [isVideo, setIsVideo] = useState(false);
  const [isArticlePdf, setIsArticlePdf] = useState(false);
  const [videoId, setVideoId] = useState('');
  const [PDFURL, setPDFURL] = useState('');
  const [PDFTitle, setPDFTitle] = useState('');
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);
  const [currentPDFPage, setCurrentPDFPage] = useState(1);
  const [totalPDFPages, setTotalPDFPages] = useState(1);
  const [isPdfDownloadAble, setIsPdfDownloadAble] = useState<boolean | undefined>(undefined);
  const [isNewsArticleDownloadAble, setIsNewsArticleDownloadAble] = useState<boolean | undefined>(undefined);
  const modalEventTimer = useRef<number | null>(null);
  const colSpan =
    records.length > 1 && selectedTab !== 'video' && menuItems.length <= 0
      ? 12
      : 24;
  const headerHeight = '106px';
  const trackCall = (mediaType: string) => {
    const seletedMenu = menuItems.find(
      (item: any) => item.key === selectedMenu
    )?.name;
    const trackingData: any = {
      media_type: mediaType,
      title: PDFTitle,
      tab_name: seletedMenu,
    };

    let engagedTime = 0;

    if (modalEventTimer.current !== null) {
      engagedTime = (Date.now() - modalEventTimer.current) / 1000;
      modalEventTimer.current = null; // Reset after usage
    }
    if (mediaType.toLowerCase() === 'pdf' || mediaType.toLowerCase() === 'news article') {
      const pdfReadPercentage = (currentPDFPage / totalPDFPages) * 100;
      trackingData.pdf_read_percentage = Number(pdfReadPercentage.toFixed(2));
    }
    if (engagedTime > 0) {
      trackingData.duration = Number(engagedTime.toFixed(2));
      if (object === 'whats-trending') {
        logWhatsTrendingEvent(trackingData);
      } else if (object === 'research-report') {
        logResearchReportEvent(trackingData);
      } else if (object === 'informed-investor') {
        logInformedInvestorEvent(trackingData);
      } else if(object === 'marketing') {
        logMarketingEvent(trackingData);
      }
    }
  };
  const onReadMoreClick = (articleHeading: string, pdfURL: string, isPdfDownloadAble?: boolean) => {
    setOpen(true);
    setIsVideo(false);
    setIsArticlePdf(false);
    setPDFURL(pdfURL);
    setPDFTitle(articleHeading);
    setIsPdfDownloadAble(isPdfDownloadAble);
    setIsNewsArticleDownloadAble(false);
  };
  const onPlayClick = (
    articleHeading: string,
    videoURL: string,
    id: string
  ) => {
    setVideoId(id);
    setOpen(true);
    setIsVideo(true);
    setPDFURL(videoURL);
    setPDFTitle(articleHeading);
  };

  const onNewsletterClick = (articleHeading: string,articleURL: string, isNewsArticleDownloadAble?: boolean) => {
    setOpen(true);
    setIsVideo(false);
    setPDFURL(articleURL);
    setIsArticlePdf(true);
    setPDFTitle(articleHeading);
    setIsNewsArticleDownloadAble(isNewsArticleDownloadAble)
    setIsPdfDownloadAble(false)
  };

  const handleOnVideoPlay = (isPlaying: string | null, item: any) => {
    // If there's already a video playing and we're switching to a different one
    if (
      playingVideoId !== null &&
      isPlaying !== null &&
      playingVideoId !== isPlaying
    ) {
      // Track the previously playing video before switching

      trackCall('Video');
      // Start tracking the new video
      modalEventTimer.current = Date.now();
    }
    // If no video was playing before and we're starting one now
    else if (playingVideoId === null && isPlaying !== null) {
      modalEventTimer.current = Date.now();
    }
    // If we're pausing the video (isPlaying becomes null)
    else if (playingVideoId !== null && isPlaying === null) {
      trackCall('Video');
      modalEventTimer.current = null;
    }

    // Update the currently playing video ID
    setPlayingVideoId(isPlaying);
  };
  return (
    <>
      <PageHeading heading={title} />
      <Layout>
        {menuItems.length > 0 && (
          <Sider
            width={220}
            style={{
              backgroundColor: 'transparent',
              maxHeight: `calc(100vh - ${headerHeight})`,
              overflow: 'auto',
            }}
          >
            <ConfigProvider
              theme={{
                components: {
                  Menu: {
                    itemSelectedBg: '#001e5a',
                    itemSelectedColor: '#FFF',
                  },
                },
              }}
            >
              <Menu
                key={Math.random()}
                className="secondary-menu"
                defaultSelectedKeys={[selectedMenu]}
                style={{
                  backgroundColor: 'transparent',
                  height: '100%',
                  borderRight: 0,
                }}
                items={menuItems}
              />
            </ConfigProvider>
          </Sider>
        )}

        {
          showMarkDown ? 
          <Content
            className="main-content scenario-container"
            style={{
              borderBottom: 'none',
              marginLeft: '20px',
              width: '100%',
              overflow: 'hidden',
              padding: '36px',
              background: '#fff',
              borderTopRightRadius: '15px',
              borderTopLeftRadius: '15px',
            }}
          >
          <div
            style={{
              height: '100%',
              overflow: 'auto',
              paddingRight: '20px',
            }}
          >
            <Title
              className="scenario-heading"
              level={4}
              style={{ marginTop: 0 }}
            >
              {"Brands Do's & Don'ts"}
            </Title>
            <div style={{ fontWeight: '500' }}>
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {brandsData &&
                  DOMpurify.sanitize(brandsData)}
              </ReactMarkdown>
            </div>
          </div>
          </Content> 
          :
        <Content
          style={{
            height: `calc(200px - ${headerHeight})`,
            marginLeft: menuItems.length > 0 ? '20px' : 0,
            //marginTop: menuItems.length > 0 ? '3px' : 0,
          }}
        >
          {
            <FilterBar
              tabs={selectedMenu === 'submenu-performance-report' ? [] : tabs}
              selectedTab={selectedTab}
              onTabClick={onTabClick}
              onAllClick={onAllClick}
              onRangeChange={onRangeChange}
              defaultDateRange={defaultDateRange}
              onPriorityChange={onPriorityChange}
              priority={priority}
              showPriorityFilter={showPriorityFilter}
              showDateRange={showDateRange}
              rangePresets={undefined}
              dateFormat={''}
              object={object}
              dateFilter={dateFilterComponent}
              selectedDateOption={selectedDateOption}
            />
          }

          {isLoading ? (
            <Loader style={{ marginTop: '20%', justifyContent: 'center' }} />
          ) : (
            <>
              <div
                style={{
                  height: `calc(100vh - ${headerPixels || '183px'})`,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  paddingRight: '10px',
                }}
              >
                {records.length > 0 ? (
                  <>
                    {records.map((item) => (
                      <>
                        {item.records.length > 0 && (
                          <>
                            <DateTitle date={item.date} />
                            <Row
                              gutter={[16, 16]}
                              style={{ marginBottom: '16px' }}
                            >
                              {item.records.map((rec: any) => {
                                return (
                                  <Col span={colSpan}>
                                    <ListItem
                                      isNewsletter={isNewsletter}
                                      key={item.id}
                                      item={{
                                        id: rec.id,
                                        title: rec.title,
                                        description: rec.description,
                                        date: rec.date,
                                        videoURL: rec.video_link,
                                        pdfURL: rec.pdf?.url,
                                        articleURL: rec?.news_article?.url,
                                        isPdfDownloadAble: rec?.is_pdf_downloadable,
                                        isNewsArticleDownloadAble: rec?.is_news_article_downloadable,
                                      }}
                                      selectedTab={selectedTab}
                                      priority={priority}
                                      onReadMoreClick={onReadMoreClick}
                                      onPlayClick={onPlayClick}
                                      onNewsletterClick={onNewsletterClick}
                                      playingVideoId={playingVideoId}
                                      onVideoPlay={(
                                        isPlaying: string | null,
                                        title: string
                                      ) => {
                                        handleOnVideoPlay(isPlaying, title);
                                        setPDFTitle(title);
                                      }}
                                      onVideoEnd={() => {
                                        trackCall('Video');
                                      }}
                                    />
                                  </Col>
                                );
                              })}
                            </Row>
                          </>
                        )}
                      </>
                    ))}
                  </>
                ) : (
                  <EmptyRecord />
                )}
              </div>
              {pagination}
            </>
          )}

          <Modal
            title={PDFTitle}
            footer={null}
            centered
            open={open}
            onCancel={() => {
              setOpen(false);
            }}
            width={isVideo ? 800 : 1200}
            afterClose={() => {
              trackCall(isVideo ? 'Video' : isArticlePdf ? 'News Article' : 'Pdf');
              setIsVideo(false);
              setIsArticlePdf(false);
              setPDFURL('');
            }}
            destroyOnClose
          >
            {isVideo ? (
              <VideoPlayer
                id={videoId}
                playingVideoId={playingVideoId}
                onVideoPlay={(id) => {
                  setPlayingVideoId(id);
                  if (id) {
                    modalEventTimer.current = Date.now();
                  }
                }}
                videoURL={PDFURL}
                onVideoPause={() => {
                  trackCall('Video');
                }}
                onVideoEnd={() => {
                  trackCall('Video');
                }}
              />
            ) : (
              <PdfViewer
                fileUrl={PDFURL}
                isPdfDownloadAble={isPdfDownloadAble}
                isNewsArticleDownloadAble={isNewsArticleDownloadAble}
                onLoad={(total) => {
                  setTotalPDFPages(total);
                  modalEventTimer.current = Date.now();
                }}
                onPageChange={(current) => {
                  if (current > currentPDFPage) {
                    setCurrentPDFPage(current);
                  }
                }}
              />
            )}
          </Modal>
        </Content>
        }
        
      </Layout>
    </>
  );
};

export default ResearchLayout;
